import { useState } from 'react';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { styles } from '~/modules/identification/forms/AdditionalInformation/styles.ts';
import Pencil from '~/components/icons/Pencil.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  TFinancialCondition,
  TFundingSources,
} from '~/modules/identification/forms/FinancialCondition/types.ts';
import { fundingSourcesText } from '../utils';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { commonStyles } from '~/theme/styles/commonStyles';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal';
import { TextInput } from '~/components/TextField/TextInput';

export const FundingSources = () => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const handleCloseModal = () => setIsModalOpened(false);

  const {
    control,
    formState: { errors, isSubmitted },
  } = useFormContext<TFinancialCondition>();

  const fundingSourcesObject: TFundingSources | null = useWatch({
    control,
    name: 'fundingSources',
  });

  return (
    <>
      <FormGridItem sx={commonStyles.centerFlexContainer}>
        <Tooltip
          title={fundingSourcesText(fundingSourcesObject as TFundingSources | null)}
          placement="top-start"
          disableFocusListener
          disableTouchListener
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -24],
                  },
                },
              ],
            },
          }}
        >
          <TextInput
            name="fundingSourcesText"
            data-testid="fundingSources"
            value={fundingSourcesText(fundingSourcesObject as TFundingSources | null)}
            fullWidth
            label={
              'Джерела надходження коштів та інших цінностей на рахунки клієнта (в тому числі які очікує клієнт, який раніше не обслуговувався)'
            }
            placeholder="Джерела надходження коштів та інших цінностей на рахунки клієнта"
            slotProps={{
              input: {
                readOnly: true,
                inputProps: {
                  onClick: () => setIsModalOpened(true),
                  'data-testid': 'fundingSources',
                },
              },
            }}
            error={isSubmitted && !!errors.fundingSources}
            helperText={isSubmitted ? errors.fundingSources?.message : undefined}
          />
        </Tooltip>
        <Box sx={{ alignSelf: 'flex-end', mb: 2.5 }}>
          <Pencil testId="fundingSources.pencil" onClick={() => setIsModalOpened(true)} />
        </Box>
      </FormGridItem>

      <BaseModal
        open={isModalOpened}
        onClose={handleCloseModal}
        data-testid="address-block-modal"
        containerProps={{ sx: styles.modalContainer }}
      >
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Джерела надходження коштів та інших цінностей на рахунки клієнта (в тому числі які очікує
          клієнт, який раніше не обслуговувався)
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox
              name={`fundingSources.businessActivity`}
              label="Від господарської діяльності"
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox
              name={`fundingSources.financialAssistance`}
              label="У вигляді фінансової допомоги"
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox
              name={`fundingSources.contractAndDerivatives`}
              label="Надходження від укладання строкових контрактів або використання інших похідних фінансових інструментів та деривативів"
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox name={`fundingSources.loan`} label="У вигляді позики" />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox
              name={`fundingSources.saleOfReceivables`}
              label="Продаж/відступлення права грошової вимоги"
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormCheckbox name={`fundingSources.saleOfSecurities`} label="Продаж цінних паперів" />
          </Grid>
          <Grid size={12} sx={commonStyles.centerFlexContainer}>
            <Typography noWrap sx={{ display: 'inline-block', pb: '14px', flexShrink: 0 }}>
              Інше (зазначте):
            </Typography>
            <FormInput name={'fundingSources.other'} variant="outlined" fullWidth />
          </Grid>
          <Box sx={styles.buttonBox} style={{ width: '100%' }}>
            <Button onClick={handleCloseModal} variant={'contained'}>
              Продовжити
            </Button>
          </Box>
        </Grid>
      </BaseModal>
    </>
  );
};
